import { useEffect, useState } from 'react';
import { api, ApiClientError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import Modal from '../../components/Modal';
import Icon from '../../components/Icon';
import { Banner, Select, TextInput } from '../../components/ui';
import {
  CREATABLE_ROLES, ROLE_BLURB, ROLE_LABEL, isAdminRole, requiredContact,
} from './roleMeta';
import { DIAL_CODES } from './dialCodes';
import type { CompanySettings, CreatedUser, ManagerOption, Role } from '../../types';

interface Props {
  /** Which kind the user set out to add; they can still switch. */
  initialRole: Role;
  managers: ManagerOption[];
  onClose: () => void;
  onCreated: () => void;
}

export default function AddUserDialog({
  initialRole, managers, onClose, onCreated,
}: Props) {
  const { can, company } = useAuth();
  const canMakeAdmins = can('company_admin', 'platform_admin');

  // Default the dial code to the company's country — so an Indian company
  // starts at +91, a UAE company at +971, etc. Falls back to +91 if the
  // country is not in the list.
  const companyDial = DIAL_CODES.find((c) => c.iso === company?.countryCode)?.dial ?? '+91';

  const [role, setRole] = useState<Role>(initialRole);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [dialCode, setDialCode] = useState(companyDial);
  const [designation, setDesignation] = useState('');
  const [employeeCode, setEmployeeCode] = useState('');
  const [reportsToId, setReportsToId] = useState('');

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [created, setCreated] = useState<CreatedUser | null>(null);

  const needs = requiredContact(role);

  // The delivery role only exists when the platform has turned the
  // dispatch module on, so it is offered only then.
  // Same for distributor — gated on the distributor module.
  const [dispatchOn, setDispatchOn] = useState(false);
  const [distributorOn, setDistributorOn] = useState(false);
  useEffect(() => {
    let cancelled = false;
    api.get<CompanySettings>('/settings')
      .then((s) => {
        if (!cancelled) {
          setDispatchOn(!!s.dispatchEnabled);
          setDistributorOn(!!s.distributorEnabled);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // A manager cannot mint an admin; the API refuses it, so the picker
  // does not offer it rather than letting them find out on submit.
  const roleOptions = CREATABLE_ROLES
    .filter((r) => canMakeAdmins || !isAdminRole(r))
    .filter((r) => r !== 'delivery' || dispatchOn)
    .filter((r) => r !== 'distributor' || distributorOn);

  useEffect(() => {
    if (!canMakeAdmins && isAdminRole(role)) setRole('sales_rep');
    if (role === 'delivery' && !dispatchOn) setRole('sales_rep');
    if (role === 'distributor' && !distributorOn) setRole('sales_rep');
  }, [canMakeAdmins, role, dispatchOn, distributorOn]);

  // Clear any lingering error (frontend or backend) when the user edits a field.
  useEffect(() => {
    setError(null);
    setFieldErrors({});
  }, [fullName, email, mobile, dialCode, role, designation, employeeCode, reportsToId]);

  function validate() {
    const errors: Record<string, string> = {};
    if (fullName.trim().length < 2) errors.fullName = 'Enter their full name';
    if (needs === 'email' && !email.trim()) {
      errors.email = 'An admin signs in with an email address, so this is required';
    }
    if (needs === 'mobile' && !mobile.trim()) {
      errors.mobile = 'A salesman signs in with their mobile number, so this is required';
    }
    if (needs === 'both') {
      if (!email.trim()) errors.email = 'A distributor signs in with email and needs a mobile for the business, so both are required';
      if (!mobile.trim()) errors.mobile = 'A distributor needs a mobile number for the business relationship';
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function submit() {
    if (!validate()) return;

    setBusy(true);
    setError(null);
    try {
      const result = await api.post<CreatedUser>('/users', {
        fullName: fullName.trim(),
        role,
        ...(email.trim() ? { email: email.trim().toLowerCase() } : {}),
        ...(mobile.trim() ? { mobile: dialCode + mobile.trim() } : {}),
        ...(designation.trim() ? { designation: designation.trim() } : {}),
        ...(employeeCode.trim() ? { employeeCode: employeeCode.trim() } : {}),
        ...(reportsToId ? { reportsToId } : {}),
      });
      setCreated(result);
    } catch (err) {
      if (err instanceof ApiClientError) {
        // VALIDATION_ERROR (Zod) returns per-field errors in `err.fields`.
        // Other API errors may put a single field name in `err.details.field`.
        if (err.fields && Object.keys(err.fields).length > 0) {
          setFieldErrors(err.fields);
        } else {
          const field = err.details?.field as string | undefined;
          if (field) setFieldErrors({ [field]: err.message });
          else setError(err.message);
        }
      } else {
        setError('Could not add them. Try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  // -- After they are created ------------------------------------------

  if (created) {
    const person = created.user;
    return (
      <Modal
        title={`${person.fullName} has been added`}
        onClose={onCreated}
        footer={(
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                // Straight into adding the next one: teams are built in
                // one sitting, not one person per visit.
                setCreated(null);
                setFullName(''); setEmail(''); setMobile('');
                setDialCode(companyDial); setDesignation(''); setEmployeeCode('');
                setFieldErrors({});
              }}
            >
              <Icon name="plus" size={15} /> Add another
            </button>
            <button type="button" className="btn btn-primary" onClick={onCreated}>Done</button>
          </>
        )}
      >
        <div className="col gap-16">
          <Banner kind="success">
            {isAdminRole(person.role) || person.role === 'distributor'
              ? `${person.fullName} can now sign in with ${person.email} and their password.`
              : `${person.fullName} can now sign in with mobile number ${person.mobile}.`}
          </Banner>

          {created.teamCode ? (
            <div className="card card-pad col gap-8">
              <span className="small faint">Team code (shown in development only)</span>
              <div className="row gap-8">
                <code className="team-code">{created.teamCode}</code>
                <button
                  type="button"
                  className="icon-btn"
                  title="Copy"
                  aria-label="Copy team code"
                  onClick={() => navigator.clipboard?.writeText(created.teamCode ?? '')}
                >
                  <Icon name="copy" size={15} />
                </button>
              </div>
              <span className="small faint">
                Expires {new Date(created.expiresAt).toLocaleDateString()}.
              </span>
            </div>
          ) : null}
        </div>
      </Modal>
    );
  }

  // -- The form ---------------------------------------------------------

  const modalTitle = role === 'distributor'
    ? 'Add a distributor'
    : isAdminRole(role) ? 'Add an admin'
    : role === 'manager' ? 'Add a manager'
    : role === 'delivery' ? 'Add a delivery person'
    : 'Add a salesman';

  return (
    <Modal
      title={modalTitle}
      subtitle="They can sign in as soon as they are added."
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void submit()}>
            {busy ? <span className="spinner" /> : null}
            Add to team
          </button>
        </>
      )}
    >
      <div className="col gap-16">
        {error ? <Banner kind="danger">{error}</Banner> : null}

        <div className="col gap-8">
          <label style={{ fontSize: 13, fontWeight: 600 }}>Role</label>
          <div className="role-picker">
            {roleOptions.map((option) => (
              <button
                key={option}
                type="button"
                data-role={option}
                className={`role-option${role === option ? ' active' : ''}`}
                onClick={() => setRole(option)}
              >
                <span className="role-option-name">{ROLE_LABEL[option]}</span>
                <span className="role-option-blurb">{ROLE_BLURB[option]}</span>
              </button>
            ))}
          </div>
        </div>

        <TextInput
          label="Full name"
          data-testid="user-name"
          value={fullName}
          autoFocus
          maxLength={120}
          placeholder="Ramesh Kadam"
          error={fieldErrors.fullName}
          onChange={(e) => setFullName(e.target.value)}
        />

        <div className="row gap-12" style={{ alignItems: 'flex-start' }}>
          <div className="col gap-4" style={{ flex: 1 }}>
            <label style={{ fontSize: 13, fontWeight: 600 }}>
              {needs === 'mobile' || needs === 'both' ? 'Mobile number' : 'Mobile number (optional)'}
            </label>
            <div className="row gap-4">
              <select
                value={dialCode}
                onChange={(e) => setDialCode(e.target.value)}
                className="input"
                style={{ width: 120, flexShrink: 0, fontSize: 13 }}
                data-testid="dial-code"
              >
                {DIAL_CODES.map((c) => (
                  <option key={c.iso} value={c.dial}>
                    {c.iso} {c.dial}
                  </option>
                ))}
              </select>
              <TextInput
                data-testid="user-mobile"
                value={mobile}
                type="tel"
                maxLength={15}
                placeholder="98765 43210"
                error={fieldErrors.mobile}
                onChange={(e) => setMobile(e.target.value)}
              />
            </div>
            {needs === 'mobile' ? (
              <span className="small faint">How they sign in to the app</span>
            ) : needs === 'both' ? (
              <span className="small faint">Required for the business relationship</span>
            ) : null}
          </div>
          <TextInput
            label={needs === 'email' || needs === 'both' ? 'Work email' : 'Email (optional)'}
            data-testid="user-email"
            value={email}
            type="email"
            maxLength={160}
            placeholder="ramesh@company.com"
            error={fieldErrors.email}
            hint={needs === 'email' || needs === 'both' ? 'How they sign in to this console' : undefined}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div className="row gap-12" style={{ alignItems: 'flex-start' }}>
          <TextInput
            label="Designation (optional)"
            value={designation}
            maxLength={80}
            placeholder="Field Officer"
            onChange={(e) => setDesignation(e.target.value)}
          />
          <TextInput
            label="Employee code (optional)"
            value={employeeCode}
            maxLength={40}
            placeholder="EMP-0142"
            onChange={(e) => setEmployeeCode(e.target.value.toUpperCase())}
          />
        </div>

        <Select
          label="Reports to (optional)"
          value={reportsToId}
          hint="Used for team attendance and area assignments."
          onChange={(e) => setReportsToId(e.target.value)}
        >
          <option value="">Nobody</option>
          {managers.map((m) => (
            <option key={m.id} value={m.id}>
              {m.fullName} — {ROLE_LABEL[m.role]}
            </option>
          ))}
        </Select>
      </div>
    </Modal>
  );
}
